'use client';

/**
 * PORTFOLIO — global site gallery from /api/portfolio (screenshots + live URLs)
 * + pull-forward site viewer (design: Control Room v4 svStyle/svPanelStyle
 * verbatim — dim+blur backdrop, 1240×740 slab scale-up summon, hairline,
 * domain pill, ● LIVE, ESC push back). Viewer shows the site's REAL capture;
 * ‹ › flick steps between sites (real data has one capture per site).
 */
import { useEffect, useState, useCallback } from 'react';
import { StatusChip, mono, display } from '@/future/primitives';

type Site = { slug: string; domain: string; screenshot?: string; liveUrl?: string; deployed?: boolean; running?: boolean };

const HEX = '#ffb454'; // factory/portfolio hue (WV in the prototype)

export function PortfolioSurface() {
  const [sites, setSites] = useState<Site[]>([]);
  const [open, setOpen] = useState<number | null>(null);
  const [fade, setFade] = useState(false);

  useEffect(() => {
    fetch('/api/portfolio').then(r => r.json()).then(d => setSites(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const step = useCallback((d: number) => {
    setOpen(o => {
      if (o === null || !sites.length) return o;
      setFade(true); setTimeout(() => setFade(false), 180);
      return (((o + d) % sites.length) + sites.length) % sites.length;
    });
  }, [sites.length]);

  // design law: ESC push back, ‹ › flick pages
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (open === null) return;
      if (e.key === 'Escape') setOpen(null);
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, step]);

  const svOpen = open !== null;
  const site = svOpen ? sites[open!] : null;

  return (
    <div className="max-w-[1280px] mx-auto">
      <div className="mb-4" style={{ ...mono(16, '.2em'), color: 'var(--uif-t5)' }}>{sites.length} WEBSITES · CLICK TO PULL FORWARD</div>
      <div className="grid grid-cols-4 gap-4">
        {sites.map((s, i) => (
          <div key={s.slug} onMouseUp={() => setOpen(i)} className="cursor-pointer">
            <div className="overflow-hidden transition-transform hover:scale-[1.03] relative" style={{
              borderRadius: 14, background: 'linear-gradient(170deg,rgba(40,140,180,.08),rgba(8,18,28,.55) 60%)',
              border: '1px solid rgba(125,232,255,.2)',
            }}>
              <div style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: 1, background: `linear-gradient(90deg,transparent,${HEX},transparent)`, zIndex: 1 }} />
              <div className="h-[150px] overflow-hidden relative" style={{ background: 'rgba(8,18,28,.6)' }}>
                {s.screenshot
                  ? <img src={s.screenshot} alt={s.domain} className="w-full h-full object-cover object-top" style={{ opacity: 0.85 }} />
                  : <div className="absolute inset-0 flex items-center justify-center" style={{ ...mono(16, '.3em'), color: 'var(--uif-t6)' }}>NO CAPTURE</div>}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 55%, rgba(4,10,18,.9))' }} />
              </div>
              <div className="p-3 flex items-center justify-between">
                <span style={{ ...display(24, 400), color: 'var(--uif-t1)' }}>{s.domain}</span>
                <StatusChip label={s.deployed ? 'LIVE' : 'DRAFT'} kind={s.deployed ? 'good' : 'dim'} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* pull-forward site viewer — svStyle/svPanelStyle verbatim */}
      <div onMouseUp={() => setOpen(null)} style={{
        position: 'fixed', inset: 0, zIndex: 57, display: 'grid', placeItems: 'center',
        background: 'rgba(1,2,9,.62)', backdropFilter: 'blur(6px)',
        opacity: svOpen ? 1 : 0, pointerEvents: svOpen ? 'auto' : 'none',
        visibility: svOpen ? 'visible' : 'hidden', transition: 'opacity .4s,visibility .4s',
      }}>
        <div onMouseUp={e => e.stopPropagation()} style={{
          position: 'relative', width: 1240, maxWidth: '90%', height: 740, maxHeight: '86%',
          boxSizing: 'border-box', borderRadius: 18, padding: '20px 26px',
          background: 'linear-gradient(170deg,rgba(40,140,180,.1),rgba(3,8,15,.95) 55%)',
          border: '1px solid rgba(125,232,255,.38)',
          boxShadow: '0 60px 140px -30px rgba(0,0,0,.95),0 0 90px -20px rgba(0,240,255,.45)',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
          transform: svOpen ? 'scale(1) translateY(0)' : 'scale(.62) translateY(90px)',
          transition: 'transform .5s cubic-bezier(.25,.9,.3,1)',
        }}>
          {/* scanlines + hairline */}
          <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(180deg,rgba(140,235,255,.04) 0 1px,transparent 1px 3px)', mixBlendMode: 'screen', opacity: .5, pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1, background: `linear-gradient(90deg,transparent,${HEX},transparent)` }} />
          {site && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                <span style={{ padding: '2px 8px', borderRadius: 999, background: 'rgba(255,180,84,.1)', border: '1px solid rgba(255,180,84,.4)', color: HEX, fontFamily: 'var(--uif-font-mono)', fontSize: 14, letterSpacing: '.14em' }}>FACTORY</span>
                <span style={{ padding: '6px 16px', borderRadius: 999, border: '1px solid rgba(125,232,255,.25)', fontFamily: 'var(--uif-font-mono)', fontSize: 18, letterSpacing: '.14em', color: '#bfeaff' }}>{site.domain}</span>
                <span style={{ fontFamily: 'var(--uif-font-mono)', fontSize: 15, letterSpacing: '.16em', color: site.deployed ? '#5df2b4' : '#ffc36b' }}>● {site.deployed ? 'LIVE' : 'DRAFT'}</span>
                <span style={{ flex: 1 }} />
                <a href={site.liveUrl || `https://${site.domain}`} target="_blank" rel="noreferrer" onMouseUp={e => e.stopPropagation()}
                  style={{ padding: '6px 14px', borderRadius: 999, color: '#04121a', background: `linear-gradient(180deg,${HEX}ee,${HEX}99)`, boxShadow: `0 0 26px ${HEX}55`, fontFamily: 'var(--uif-font-mono)', fontSize: 15, letterSpacing: '.14em', textDecoration: 'none' }}>OPEN LIVE ↗</a>
                <button onMouseUp={() => setOpen(null)} aria-label="Close viewer" style={{
                  width: 24, height: 24, borderRadius: 7, border: '1px solid rgba(125,232,255,.3)', display: 'grid', placeItems: 'center',
                  color: '#7fa9bd', fontSize: 26, lineHeight: 1, background: 'transparent', transition: 'all .2s', marginLeft: 8,
                }}>×</button>
              </div>
              <div style={{ flex: 1, minHeight: 0, marginTop: 16, borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(125,232,255,.16)', opacity: fade ? 0 : 1, transition: 'opacity .18s', background: 'rgba(8,18,28,.5)' }}>
                {site.screenshot
                  ? <img src={site.screenshot} alt={site.domain} className="w-full h-full object-cover object-top" />
                  : <div className="w-full h-full flex items-center justify-center" style={{ ...mono(18, '.3em'), color: 'var(--uif-t6)' }}>NO CAPTURE YET</div>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 22, paddingTop: 14, flexShrink: 0, fontFamily: 'var(--uif-font-mono)', fontSize: 16, letterSpacing: '.16em', color: '#56788c' }}>
                <span>SITE <span style={{ color: '#9ef0ff' }}>{(open ?? 0) + 1} / {sites.length}</span></span>
                <span style={{ flex: 1 }} />
                <span style={{ color: '#3d4654' }}>‹ › FLICK SITES · ESC PUSH BACK</span>
              </div>
              {/* edge flick zones */}
              <div onMouseUp={e => { e.stopPropagation(); step(-1); }} style={{ position: 'absolute', left: 10, top: '46%', width: 44, height: 84, display: 'grid', placeItems: 'center', opacity: .8, cursor: 'pointer' }}>
                <svg width="20" height="36" viewBox="0 0 26 46" fill="none" stroke="rgba(158,240,255,.7)" strokeWidth="2"><path d="M20 6 L7 23 L20 40" /></svg>
              </div>
              <div onMouseUp={e => { e.stopPropagation(); step(1); }} style={{ position: 'absolute', right: 10, top: '46%', width: 44, height: 84, display: 'grid', placeItems: 'center', opacity: .8, cursor: 'pointer' }}>
                <svg width="20" height="36" viewBox="0 0 26 46" fill="none" stroke="rgba(158,240,255,.7)" strokeWidth="2"><path d="M6 6 L19 23 L6 40" /></svg>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
